import { useEffect, useRef, useState } from 'react'
import posthog from 'posthog-js'
import { toast } from 'react-toastify'
import { locateFundTransaction } from 'services/fund'

/** «in-123» / «out-45» — what a row's `transactionKey` is. */
const KEY_PATTERN = /^(in|out)-\d+$/

/** How long the row stays lit after the scroll. */
export const FOCUS_HIGHLIGHT_MS = 4000

/**
 * `?tx=in-123` — a notification's «🧾 مشاهدهٔ تراکنش» opened the fund on one
 * transaction. Ask the server which page it is on, load the list that far,
 * then scroll to the row and light it up for a moment.
 *
 * The list is the ordinary infinite list: it only ever loads forward, and the
 * row is found by the same key the list renders, so nothing here can show a
 * row the list itself would not.
 */
export default function useFocusTransaction({
    fundId,
    focusKey,
    rowKeys,
    loadedPages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    enabled,
}: {
    fundId: string | undefined
    focusKey: string | null
    rowKeys: string[]
    loadedPages: number
    hasNextPage: boolean
    isFetchingNextPage: boolean
    fetchNextPage: () => void
    enabled: boolean
}) {
    const key = focusKey && KEY_PATTERN.test(focusKey) ? focusKey : null
    const [target, setTarget] = useState<{ page: number } | null>(null)
    const [highlighted, setHighlighted] = useState<string | null>(null)
    const done = useRef<string | null>(null)
    const asked = useRef<string | null>(null)

    // 1. where is it?
    useEffect(() => {
        if (!key || !enabled || !fundId || asked.current === key) return
        asked.current = key
        locateFundTransaction(fundId, key)
            .then(({ page, found }) => {
                if (found) {
                    setTarget({ page })
                    return
                }
                done.current = key
                toast.info('این تراکنش در فهرست پیدا نشد.')
                posthog.capture('fund_transaction_focused', {
                    moneypool_id: Number(fundId),
                    found: false,
                })
            })
            .catch(() => {
                // the list still opens; only the jump is lost
                done.current = key
            })
    }, [key, enabled, fundId])

    // 2. load that far
    useEffect(() => {
        if (!key || !target || done.current === key) return
        if (rowKeys.includes(key)) return
        if (loadedPages < target.page && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
    }, [
        key,
        target,
        rowKeys,
        loadedPages,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    ])

    // 3. scroll and light it up
    useEffect(() => {
        if (!key || done.current === key || !rowKeys.includes(key)) return
        const row = document.getElementById(`tx-${key}`)
        if (!row) return
        done.current = key
        row.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
        setHighlighted(key)
        posthog.capture('fund_transaction_focused', {
            moneypool_id: Number(fundId),
            found: true,
            page: target?.page ?? 1,
        })
    }, [key, rowKeys, fundId, target])

    // its own effect: the list re-renders while the light is on
    useEffect(() => {
        if (!highlighted) return
        const timer = window.setTimeout(
            () => setHighlighted(null),
            FOCUS_HIGHLIGHT_MS
        )
        return () => window.clearTimeout(timer)
    }, [highlighted])

    return { highlighted }
}
